import { consola } from 'consola'
import { apiCart } from './apiCart'

const logger = consola.withTag('cart-quantity-service')

/**
 * Сервис синхронизации количества товаров в корзине.
 * POST /cart принимает quantity как дельту, а не абсолютное значение.
 *
 * @principle SRP — отвечает только за синхронизацию количества
 */
export class CartQuantityService {
  async setExactQuantity(params: {
    productId: string
    branchId: string
    currentQuantity: number
    targetQuantity: number
    addons?: string[]
    excludes?: string[]
  }): Promise<void> {
    const { productId, branchId, currentQuantity, targetQuantity, addons = [], excludes = [] } = params

    if (targetQuantity < 0) throw new Error(`Target quantity cannot be negative: ${targetQuantity}`)
    if (currentQuantity < 0) throw new Error(`Current quantity cannot be negative: ${currentQuantity}`)

    const delta = targetQuantity - currentQuantity
    const deltaRounded = Math.round(delta * 100) / 100

    if (Math.abs(deltaRounded) < 0.01) {
      logger.debug('Delta is 0, skipping', { productId, currentQuantity, targetQuantity })
      return
    }

    const amount = Math.abs(deltaRounded)

    if (deltaRounded > 0) {
      await apiCart.increaseQuantity({ product_id: productId, branch_id: branchId, amount, addons, excludes })
      logger.success('Quantity increased', { productId, from: currentQuantity, to: targetQuantity })
    }
    else {
      await apiCart.decreaseQuantity({ product_id: productId, branch_id: branchId, amount, addons, excludes })
      logger.success('Quantity decreased', { productId, from: currentQuantity, to: targetQuantity })
    }
  }

  async syncItemDuringTransfer(params: {
    productId: string
    branchId: string
    localQuantity: number
    serverQuantity: number
    addons?: string[]
    excludes?: string[]
  }): Promise<void> {
    const { productId, branchId, localQuantity, serverQuantity, addons = [], excludes = [] } = params

    if (localQuantity < 0 || serverQuantity < 0) {
      throw new Error(`Invalid quantities: local=${localQuantity}, server=${serverQuantity}`)
    }

    if (localQuantity === 0) return

    await apiCart.addToCart({ product_id: productId, branch_id: branchId, quantity: localQuantity, addons, excludes })
    logger.success('Item synced', { productId, added: localQuantity, total: serverQuantity + localQuantity })
  }

  async getServerQuantity(params: {
    branchId: string
    productId: string
    addons?: string[]
    excludes?: string[]
  }): Promise<number> {
    const { branchId, productId, addons = [], excludes = [] } = params

    try {
      const cartData = await apiCart.getCart(branchId)
      const normalizedAddons = addons.map(String).sort()
      const normalizedExcludes = excludes.map(String).sort()

      const item = cartData.products.find(
        p =>
          String(p.id) === String(productId)
          && JSON.stringify((p.addons ?? []).map(String).sort()) === JSON.stringify(normalizedAddons)
          && JSON.stringify((p.excludes ?? []).map(String).sort()) === JSON.stringify(normalizedExcludes),
      )

      return item ? Number(item.quantity) : 0
    }
    catch (error: any) {
      logger.warn('Failed to get server quantity (non-critical)', { productId, error: error?.message })
      return 0
    }
  }
}

export const cartQuantityService = new CartQuantityService()
